<template>
    <v-dialog v-model="internalDialog" persistent max-width="700">
        <v-card>
            <v-card-title class="headline">
                <v-icon left>mdi-plus-circle</v-icon>
                {{ getDialogTitle() }}
                <v-spacer></v-spacer>
                <v-btn-toggle v-model="mode" mandatory>
                    <v-btn value="single" small>
                        <v-icon small>mdi-file-plus</v-icon>
                        Single
                    </v-btn>
                    <v-btn value="multiple" small>
                        <v-icon small>mdi-file-multiple</v-icon>
                        Multiple
                    </v-btn>
                </v-btn-toggle>
            </v-card-title>

            <v-card-text class="mt-3">
                <v-form ref="taskForm" v-model="formValid">
                    <v-row>
                        <!-- Goal Selector (for milestones) -->
                        <v-col cols="12" v-if="goals && goals.length > 0">
                            <v-autocomplete v-model="taskData.goal_id" :items="goals" item-text="name" item-value="id"
                                :label="taskType === 'milestone' ? 'Goal *' : 'Goal (Optional)'"
                                :rules="taskType === 'milestone' ? [v => !!v || 'Goal is required for milestones'] : []"
                                outlined dense :clearable="taskType !== 'milestone'">
                                <template v-slot:selection="{ item }">
                                    {{ item.name }}
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Assignees Selection (shown only if user is manager of selected role) -->
                        <v-col cols="12" v-if="shouldShowAssignees">
                            <v-autocomplete v-model="taskData.assignee_ids" :items="searchableUsers" item-text="name"
                                item-value="id" multiple chips deletable-chips label="Assignees *" outlined dense
                                :rules="assigneeRules" :error="!taskData.assignee_ids.length && validationAttempted"
                                placeholder="Search by name, role, email, or position"
                                :search-input.sync="assigneeSearch" :filter="customFilter" hide-no-data clearable>
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index < 3" small close @click:close="removeAssignee(item.id)"
                                        :color="getAvatarColor(item.name)" text-color="white">
                                        <v-avatar left size="20">
                                            <span class="assignee-initials">{{ getInitials(item.name) }}</span>
                                        </v-avatar>
                                        {{ item.name }}
                                    </v-chip>
                                    <span v-if="index === 3" class="text-caption">
                                        (+{{ taskData.assignee_ids.length - 3 }} more)
                                    </span>
                                </template>

                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-bind="attrs" v-on="on">
                                        <v-list-item-avatar size="32">
                                            <v-avatar size="32" :color="getAvatarColor(item.name)">
                                                <span class="assignee-initials">{{ getInitials(item.name) }}</span>
                                            </v-avatar>
                                        </v-list-item-avatar>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                                            <v-list-item-subtitle>
                                                <div class="user-role-info">
                                                    <span v-if="item.position_name || item.role?.name"
                                                        class="role-position">
                                                        {{ item.position_name || item.role?.name || 'No Position' }}
                                                    </span>
                                                    <span v-if="item.email" class="user-email">{{ item.email }}</span>
                                                </div>
                                            </v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Task Names - Single Mode -->
                        <v-col v-if="mode === 'single'" cols="12">
                            <v-textarea v-model="taskData.name" label="Task Name *" outlined dense :rules="nameRules"
                                :error="!taskData.name && validationAttempted"
                                placeholder="Enter task name" rows="1" auto-grow></v-textarea>
                        </v-col>

                        <!-- Task Names - Multiple Mode -->
                        <v-col v-if="mode === 'multiple'" cols="12">
                            <v-textarea v-model="taskNamesText" label="Task Names (one per line) *" outlined dense
                                rows="4" :rules="multipleNamesRules" :error="!taskNamesText && validationAttempted"
                                placeholder="Enter task names, one per line:&#10;Task 1&#10;Task 2&#10;Task 3"
                                hint="Enter each task name on a separate line" persistent-hint></v-textarea>
                            <div v-if="taskNamesList.length > 0" class="mt-2">
                                <v-chip-group column>
                                    <v-chip v-for="(name, index) in taskNamesList" :key="index" small outlined close
                                        @click:close="removeTaskName(index)">
                                        {{ name }}
                                    </v-chip>
                                </v-chip-group>
                                <div class="text-caption text--secondary mt-1">
                                    {{ taskNamesList.length }} tasks will be created
                                </div>
                            </div>
                        </v-col>

                        <!-- Description -->
                        <v-col cols="12">
                            <v-textarea v-model="taskData.description" label="Description" outlined dense rows="3"
                                placeholder="Enter task description (optional)"></v-textarea>
                        </v-col>

                        <!-- Priority and Due Date -->
                        <v-col cols="6">
                            <v-select v-model="taskData.priority_id" :items="priorities" item-text="name"
                                item-value="id" label="Priority" outlined dense clearable>
                                <template v-slot:selection="{ item }">
                                    <v-chip small :color="getPriorityChipColor(item.id)"
                                        :text-color="getPriorityTextColor(item.id)">
                                        {{ item.name }}
                                    </v-chip>
                                </template>
                            </v-select>
                        </v-col>

                        <v-col cols="6">
                            <v-menu v-model="dueDateMenu" :close-on-content-click="false" transition="scale-transition"
                                offset-y max-width="290px" min-width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="taskData.due_date" 
                                        :label="taskData.status_id === 7 ? 'Due Date' : 'Due Date *'" 
                                        readonly outlined dense
                                        v-bind="attrs" v-on="on" :rules="dueDateRules"
                                        :error="!taskData.due_date && validationAttempted && taskData.status_id !== 7"
                                        placeholder="Select due date"></v-text-field>
                                </template>
                                <v-date-picker v-model="taskData.due_date" @input="dueDateMenu = false" no-title
                                    scrollable></v-date-picker>
                            </v-menu>
                        </v-col>
                        
                        <!-- Start Time -->
                        <v-col cols="6" v-if="taskType !== 'assign' && taskType !== 'self'">
                            <v-menu 
                                v-model="startTimeMenu" 
                                :close-on-content-click="false" 
                                transition="scale-transition"
                                offset-y 
                                max-width="290px" 
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field 
                                        :value="formattedStartTime" 
                                        label="Start" 
                                        readonly 
                                        outlined 
                                        dense
                                        clearable
                                        @click:clear="clearStartTime"
                                        v-bind="attrs" 
                                        v-on="on"
                                        prepend-inner-icon="mdi-clock-start"
                                        placeholder="Select start time (optional)"
                                    ></v-text-field>
                                </template>
                                <v-card>
                                    <v-date-picker 
                                        v-model="startDate" 
                                        no-title
                                        scrollable
                                    ></v-date-picker>
                                    <v-time-picker 
                                        v-model="startTime" 
                                        :format="$tz.pickerFormat"
                                        scrollable
                                    ></v-time-picker>
                                    <v-card-actions>
                                        <v-spacer />
                                        <v-btn text @click="cancelStartTime">Cancel</v-btn>
                                        <v-btn color="primary" text @click="updateStartDateTime">OK</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-menu>
                        </v-col>
                        
                        <!-- End Time -->
                        <v-col cols="6" v-if="taskType !== 'assign' && taskType !== 'self'">
                            <v-menu 
                                v-model="endTimeMenu" 
                                :close-on-content-click="false" 
                                transition="scale-transition"
                                offset-y 
                                max-width="290px" 
                                min-width="290px"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field 
                                        :value="formattedEndTime" 
                                        label="End" 
                                        readonly 
                                        outlined 
                                        dense
                                        clearable
                                        @click:clear="clearEndTime"
                                        v-bind="attrs" 
                                        v-on="on"
                                        prepend-inner-icon="mdi-clock-end"
                                        placeholder="Select end time (optional)"
                                    ></v-text-field>
                                </template>
                                <v-card>
                                    <v-date-picker 
                                        v-model="endDate" 
                                        no-title
                                        scrollable
                                    ></v-date-picker>
                                    <v-time-picker 
                                        v-model="endTime" 
                                        :format="$tz.pickerFormat"
                                        scrollable
                                    ></v-time-picker>
                                    <v-card-actions>
                                        <v-spacer />
                                        <v-btn text @click="cancelEndTime">Cancel</v-btn>
                                        <v-btn color="primary" text @click="updateEndDateTime">OK</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-menu>
                        </v-col>
                        
                        <!-- Allocated Time -->
                        <v-col cols="6" v-if="taskType !== 'assign' && taskType !== 'self'">
                            <v-text-field 
                                :value="calculatedAllocatedTime" 
                                label="Allocated" 
                                outlined 
                                dense
                                prepend-inner-icon="mdi-timer-outline"
                                readonly
                                placeholder="Auto-calculated"
                                :hint="hasNegativeDuration ? '' : 'Duration between start and end time'"
                                :error-messages="hasNegativeDuration ? 'End time must be after start time' : ''"
                                :error="hasNegativeDuration"
                                persistent-hint
                            ></v-text-field>
                        </v-col>

                        <!-- Status (shown for approval workflow and self-assigned tasks) -->
                        <v-col cols="6" v-if="isApprovalMode || taskType === 'approval' || taskType === 'self'">
                            <v-select v-model="taskData.status_id" :items="statuses" item-text="name" item-value="id"
                                label="Status" outlined dense clearable>
                                <template v-slot:selection="{ item }">
                                    <v-chip small :color="getStatusChipColor(item.id)"
                                        :text-color="getStatusTextColor(item.id)">
                                        {{ item.name }}
                                    </v-chip>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Recurrence Pattern (shown only when status is recurring - status_id = 7) -->
                        <v-col cols="12" v-if="taskData.status_id === 7">
                            <v-card outlined class="pa-4">
                                <v-card-title class="text-subtitle-1 pa-0 mb-3">
                                    <v-icon class="mr-2" color="primary">mdi-repeat</v-icon>
                                    Recurrence Pattern
                                </v-card-title>

                                <v-row>
                                    <!-- Every X weeks -->
                                    <v-col cols="12" md="4">
                                        <div class="d-flex align-center">
                                            <span class="text-body-2 mr-2">Every</span>
                                            <v-text-field
                                                v-model.number="taskData.recurrence_interval"
                                                type="number"
                                                min="1"
                                                max="52"
                                                outlined
                                                dense
                                                hide-details
                                                style="max-width: 80px;"
                                                :rules="[rules.required, rules.positiveNumber]"
                                            ></v-text-field>
                                            <span class="text-body-2 ml-2">week(s)</span>
                                        </div>
                                    </v-col>

                                    <!-- Days of Week -->
                                    <v-col cols="12" md="8">
                                        <div class="d-flex align-center mb-2" style="gap: 8px;">
                                            <div class="text-body-2">On these days:</div>
                                            <v-btn 
                                                x-small 
                                                outlined 
                                                color="primary" 
                                                @click="selectWeekdays"
                                                style="height: 24px; font-size: 0.75rem;"
                                            >
                                                Daily
                                            </v-btn>
                                            <v-btn 
                                                x-small 
                                                outlined 
                                                color="secondary" 
                                                @click="clearAllDays"
                                                style="height: 24px; font-size: 0.75rem;"
                                            >
                                                Clear
                                            </v-btn>
                                        </div>
                                        <div class="d-flex flex-wrap" style="gap: 8px;">
                                            <v-chip
                                                v-for="day in daysOfWeek"
                                                :key="day.value"
                                                :color="taskData.recurrence_days.includes(day.value) ? 'primary' : 'default'"
                                                :text-color="taskData.recurrence_days.includes(day.value) ? 'white' : 'black'"
                                                small
                                                clickable
                                                @click="toggleRecurrenceDay(day.value)"
                                            >
                                                {{ day.label }}
                                            </v-chip>
                                        </div>
                                    </v-col>

                                    <!-- Series Start Date -->
                                    <v-col cols="12" md="6">
                                        <v-menu
                                            v-model="seriesStartDateMenu"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            max-width="290px"
                                            min-width="290px"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="taskData.series_start_date"
                                                    label="Series Start Date"
                                                    prepend-inner-icon="mdi-calendar"
                                                    readonly
                                                    outlined
                                                    dense
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :rules="[rules.required]"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="taskData.series_start_date"
                                                :min="todayDate"
                                                @input="seriesStartDateMenu = false"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>

                                    <!-- Series End Date (Optional) -->
                                    <v-col cols="12" md="6">
                                        <v-menu
                                            v-model="seriesEndDateMenu"
                                            :close-on-content-click="false"
                                            transition="scale-transition"
                                            offset-y
                                            max-width="290px"
                                            min-width="290px"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="taskData.series_end_date"
                                                    label="Series End Date (Optional)"
                                                    prepend-inner-icon="mdi-calendar"
                                                    readonly
                                                    outlined
                                                    dense
                                                    clearable
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :rules="[rules.endDateAfterStart]"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="taskData.series_end_date"
                                                :min="taskData.series_start_date || todayDate"
                                                @input="seriesEndDateMenu = false"
                                            ></v-date-picker>
                                        </v-menu>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-col>

                        <!-- Company (if applicable) -->
                        <v-col cols="6" v-if="taskType !== 'self'">
                            <v-autocomplete v-model="taskData.company_id" :items="companiesData" item-text="name" item-value="id"
                                label="Company" outlined dense :disabled="taskType === 'milestone'">
                                <template v-slot:selection="{ item }">
                                    {{ item.name }}
                                </template>
                            </v-autocomplete>
                        </v-col>

                        <!-- Role Selector (hidden if task is for a meeting or self task) -->
                        <v-col :cols="6" v-if="(!meetingId || taskType === 'milestone') && taskType !== 'self'">
                            <v-select v-model="taskData.role_id" :items="filteredRoles" item-text="name" item-value="id"
                                label="Assign to Role" outlined dense clearable>
                                <template v-slot:selection="{ item }">
                                    {{ item.name }}
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                                        <v-list-item-subtitle v-if="managedRoleIds.includes(item.id)">
                                            {{ getRoleMemberCount(item.id) }} member(s) - You manage this role
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Attachments Section -->
                        <v-col cols="12">
                            <div class="attachment-section">
                                <div class="d-flex align-center justify-space-between mb-3">
                                    <label class="text-subtitle-2 font-weight-medium">Attachments</label>
                                    <v-btn 
                                        small 
                                        :color="showAttachResourceDialog ? 'error' : 'primary'"
                                        outlined 
                                        @click="showAttachResourceDialog = !showAttachResourceDialog"
                                        class="attach-btn"
                                    >
                                        <v-icon left small>{{ showAttachResourceDialog ? 'mdi-close' : 'mdi-paperclip' }}</v-icon>
                                        {{ showAttachResourceDialog ? 'Cancel' : 'Attach Resources' }}
                                    </v-btn>
                                </div>

                                <!-- Embedded Attach Resource Component -->
                                <div v-if="showAttachResourceDialog" class="mb-4">
                                    <AttachResourceDialogEmbedded
                                        :embedded="true"
                                        :default-company-id="taskData.company_id"
                                        :already-attached="attachedResources"
                                        @resources-attached="handleResourcesAttached"
                                        @close="showAttachResourceDialog = false"
                                    />
                                </div>

                                <!-- Attached Resources Display -->
                                <div v-if="attachedResources.length > 0" class="attached-resources">
                                    <div 
                                        v-for="resource in attachedResources" 
                                        :key="resource.id" 
                                        class="attachment-chip"
                                    >
                                        <v-chip 
                                            close 
                                            @click:close="removeAttachment(resource.id)"
                                            color="blue lighten-4"
                                            text-color="blue darken-2"
                                            class="ma-1"
                                        >
                                            <v-icon left small :color="getFileTypeColor(resource.file_type)">
                                                {{ getFileTypeIcon(resource.file_type) }}
                                            </v-icon>
                                            {{ resource.name }}
                                        </v-chip>
                                    </div>
                                </div>

                                <!-- No attachments message -->
                                <div v-else-if="!showAttachResourceDialog" class="no-attachments">
                                    <p class="text-caption grey--text ma-0">No attachments added</p>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="closeDialog" text :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn @click="createTasks" color="primary" :loading="loading" :disabled="!canCreate">
                    <v-icon left>mdi-plus</v-icon>
                    {{ getCreateButtonText() }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Embedded Attach Resource Dialog - No longer needed -->
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import AttachResourceDialogEmbedded from './AttachResourceDialogEmbedded.vue';

export default {
    name: "TaskCreationDialog",
    inject: {
        allManagers: {
            from: 'allManagers',
            default() {
                return () => [];
            }
        }
    },
    components: {
        AttachResourceDialogEmbedded
    },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        users: {
            type: Array,
            default: () => []
        },
        defaultAssignees: {
            type: Array,
            default: () => []
        },
        companies: {
            type: Array,
            default: () => []
        },
        taskType: {
            type: String,
            default: 'assign', // 'self', 'assign', 'approval', or 'milestone'
            validator: value => ['self', 'assign', 'approval', 'milestone'].includes(value)
        },
        isApprovalMode: {
            type: Boolean,
            default: false
        },
        meetingId: {
            type: [Number, String],
            default: null
        },
        goals: {
            type: Array,
            default: () => []
        },
        defaultGoalId: {
            type: [Number, String],
            default: null
        },
        defaultCompanyId: {
            type: [Number, String],
            default: null
        },
        defaultRoleId: {
            type: [Number, String],
            default: null
        },
        defaultStatusId: {
            type: [Number, String],
            default: null
        },
        defaultName: {
            type: String,
            default: ''
        },
        defaultDescription: {
            type: String,
            default: ''
        },
        defaultPriorityId: {
            type: [Number, String],
            default: null
        },
        defaultDueDate: {
            type: String,
            default: null
        },
        defaultMode: {
            type: String,
            default: 'single' // 'single' or 'multiple'
        },
        defaultTaskNames: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            mode: 'single', // 'single' or 'multiple'
            loading: false,
            formValid: false,
            validationAttempted: false,
            dueDateMenu: false,
            startTimeMenu: false,
            endTimeMenu: false,
            startDate: null,
            startTime: null,
            endDate: null,
            endTime: null,
            seriesStartDateMenu: false,
            seriesEndDateMenu: false,
            taskData: {
                name: '',
                description: '',
                assignee_ids: [],
                priority_id: null,
                status_id: null,
                due_date: null,
                start_time: null,
                end_time: null,
                allocated_time: null,
                company_id: null,
                goal_id: null,
                role_id: null,
                // Recurrence fields
                recurrence_interval: 1,
                recurrence_days: [],
                series_start_date: null,
                series_end_date: null
            },
            taskNamesText: '',
            assigneeSearch: '',
            assigneeRules: [
                v => v.length > 0 || 'At least one assignee is required'
            ],
            nameRules: [
                v => !!v || 'Task name is required'
            ],
            multipleNamesRules: [
                v => !!v || 'At least one task name is required'
            ],
            rules: {
                required: value => !!value || 'This field is required',
                positiveNumber: value => {
                    if (!value) return true;
                    return value > 0 || 'Must be a positive number';
                },
                endDateAfterStart: value => {
                    if (!value || !this.taskData.series_start_date) return true;
                    return this.taskData.series_start_date <= value || 'End date must be after start date';
                }
            },
            daysOfWeek: [
                { label: 'Mon', value: 1 },
                { label: 'Tue', value: 2 },
                { label: 'Wed', value: 3 },
                { label: 'Thu', value: 4 },
                { label: 'Fri', value: 5 },
                { label: 'Sat', value: 6 },
                { label: 'Sun', value: 0 }
            ],
            // Attachment-related data
            showAttachResourceDialog: false,
            attachedResources: []
        };
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_selected_company: "companies/get_selected_company",
            get_priorities: "tasks/get_priorities",
            get_statuses: "tasks/get_statuses",
            get_roles: "auth/get_roles"
        }),
        internalDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        },
        availableUsers() {
            if (this.taskType === 'self') {
                return [this.get_user];
            }
            return this.users || [];
        },
        searchableUsers() {
            let usersToShow = this.availableUsers;
            
            // Check if user is CEO or COO
            const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
            const cooEmail = process.env.VUE_APP_COO_EMAIL;
            const userEmail = this.get_user?.email?.toLowerCase();
            const isCeoOrCoo = userEmail && (
                (ceoEmail && userEmail === ceoEmail.toLowerCase()) || 
                (cooEmail && userEmail === cooEmail.toLowerCase())
            );
            
            // CEO/COO can see all users, ignore role filtering
            if (isCeoOrCoo) {
                return usersToShow.map(user => ({
                    ...user,
                    searchableText: [
                        user.name || '',
                        user.email || '',
                        user.position_name || '',
                        user.role?.name || ''
                    ].join(' ').toLowerCase()
                }));
            }
            
            // If manager has selected a role they manage, filter to show only users from that role
            if (this.taskType === 'assign' && this.isManagerOfSelectedRole && this.taskData.role_id) {
                usersToShow = this.availableUsers.filter(user => user.role_id === this.taskData.role_id);
            }
            
            return usersToShow.map(user => ({
                ...user,
                searchableText: [
                    user.name || '',
                    user.email || '',
                    user.position_name || '',
                    user.role?.name || ''
                ].join(' ').toLowerCase()
            }));
        },
        priorities() {
            return this.get_priorities;
        },
        statuses() {
            return this.get_statuses;
        },
        companiesData() {
            return this.companies || [];
        },
        selectedCompany() {
            return this.get_selected_company;
        },
        formattedStartTime() {
            if (!this.taskData.start_time) return '';
            return this.$date(this.taskData.start_time).format(`YYYY-MM-DD ${this.$tz.time}`);
        },
        formattedEndTime() {
            if (!this.taskData.end_time) return '';
            return this.$date(this.taskData.end_time).format(`YYYY-MM-DD ${this.$tz.time}`);
        },
        calculatedAllocatedTime() {
            if (!this.taskData.start_time || !this.taskData.end_time) {
                return null;
            }
            
            const start = this.$date(this.taskData.start_time);
            const end = this.$date(this.taskData.end_time);
            
            if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
                return null;
            }
            
            // Calculate duration in milliseconds and convert to hours/minutes
            const diffMs = end.diff(start);
            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            
            return `${hours}h ${minutes}m`;
        },
        hasNegativeDuration() {
            if (!this.taskData.start_time || !this.taskData.end_time) {
                return false;
            }
            
            const start = this.$date(this.taskData.start_time);
            const end = this.$date(this.taskData.end_time);
            
            if (!start.isValid() || !end.isValid()) {
                return false;
            }
            
            return end.isBefore(start);
        },
        filteredRoles() {
            // Exclude Admin (id: 1), Manager (id: 2), and Super Admin (id: 10) roles
            const roles = this.get_roles || [];
            return roles.filter(role => role.id !== 1 && role.id !== 2 && role.id !== 10);
        },
        todayDate() {
            return this.$date().format('YYYY-MM-DD');
        },
        isCurrentUserManager() {
            // Check if current user is a member for any role
            if (!this.allManagers || typeof this.allManagers !== 'function') return false;
            const managers = this.allManagers() || [];
            return managers.some(m => m.user_id === this.get_user?.id);
        },
        currentUserRoleId() {
            // Get the role ID that the current user is a member of
            if (!this.allManagers || typeof this.allManagers !== 'function') return null;
            const managers = this.allManagers() || [];
            const memberEntry = managers.find(m => m.user_id === this.get_user?.id);
            return memberEntry ? memberEntry.user_role_id : null;
        },
        managedRoleIds() {
            // Get all role IDs that the current user is a member of
            if (!this.allManagers || typeof this.allManagers !== 'function') return [];
            const managers = this.allManagers() || [];
            return managers
                .filter(m => m.user_id === this.get_user?.id)
                .map(m => m.user_role_id);
        },
        isManagerOfSelectedRole() {
            // Check if current user manages the selected role
            if (!this.taskData.role_id) return false;
            return this.managedRoleIds.includes(this.taskData.role_id);
        },
        usersInManagerRole() {
            // Get all users in the same role as the manager
            if (!this.currentUserRoleId) return [];
            return this.users.filter(user => user.role_id === this.currentUserRoleId);
        },
        managerRoleOptions() {
            // Return only the role(s) that the current user manages
            if (!this.isCurrentUserManager || !this.currentUserRoleId) return [];
            const roles = this.get_roles || [];
            const managedRole = roles.find(r => r.id === this.currentUserRoleId);
            return managedRole ? [managedRole] : [];
        },
        shouldShowAssignees() {
            // Show assignees for approval mode
            if (this.isApprovalMode || this.taskType === 'approval') return true;
            
            // Check if user is CEO or COO (configured in .env)
            const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
            const cooEmail = process.env.VUE_APP_COO_EMAIL;
            console.log('CEO Email from .env:', ceoEmail);
            const userEmail = this.get_user?.email?.toLowerCase();
            
            if (userEmail && (
                (ceoEmail && userEmail === ceoEmail.toLowerCase()) || 
                (cooEmail && userEmail === cooEmail.toLowerCase())
            )) {
                return true;
            }
            
            // For assign mode, show only if user is manager of the SELECTED role
            if (this.taskType === 'assign') {
                // If no role selected, don't show assignees
                if (!this.taskData.role_id) return false;
                
                // Check if current user manages the selected role
                return this.isManagerOfSelectedRole;
            }
            
            return false;
        },
        taskNamesList() {
            if (this.mode === 'single') return [];
            return this.taskNamesText
                .split('\n')
                .map(name => name.trim())
                .filter(name => name.length > 0);
        },
        dueDateRules() {
            // If status is 7 (recurring), due date is not required
            if (this.taskData.status_id === 7) {
                return [];
            }
            return [v => !!v || 'Due date is required'];
        },
        canCreate() {
            const dueDateRequired = this.taskData.status_id !== 7;
            if (this.mode === 'single') {
                return this.taskData.name &&
                    // this.taskData.assignee_ids.length > 0 &&
                    (!dueDateRequired || this.taskData.due_date);
            } else {
                return this.taskNamesList.length > 0 &&
                    // this.taskData.assignee_ids.length > 0 &&
                    (!dueDateRequired || this.taskData.due_date);
            }
        },
        allUsers() {
            return this.users || [];
        },
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.resetForm();
            }
        },
        taskType: {
            handler() {
                this.setupDefaultAssignees();
            },
            immediate: true
        },
        'taskData.role_id'(newRoleId) {
            // When role is selected by manager, set status to "To Do" but don't auto-populate assignees
            if (newRoleId && this.isCurrentUserManager && this.currentUserRoleId === newRoleId) {
                // Set status to "To Do" (status_id: 1)
                this.taskData.status_id = 1;
                // Clear assignees so manager can manually select
                this.taskData.assignee_ids = [];
            } else if (!newRoleId) {
                // When role is cleared, reset to default assignees
                this.setupDefaultAssignees();
            }
        },
        startTimeMenu(isOpen) {
            if (isOpen) {
                // Initialize picker values when menu opens
                if (this.taskData.start_time) {
                    const datetime = this.$date(this.taskData.start_time);
                    this.startDate = datetime.format('YYYY-MM-DD');
                    this.startTime = datetime.format('HH:mm');
                } else if (!this.startDate || !this.startTime) {
                    this.startDate = this.$date().format('YYYY-MM-DD');
                    this.startTime = '09:00';
                }
            }
        },
        endTimeMenu(isOpen) {
            if (isOpen) {
                // Initialize picker values when menu opens
                if (this.taskData.end_time) {
                    const datetime = this.$date(this.taskData.end_time);
                    this.endDate = datetime.format('YYYY-MM-DD');
                    this.endTime = datetime.format('HH:mm');
                } else if (!this.endDate || !this.endTime) {
                    this.endDate = this.$date().format('YYYY-MM-DD');
                    this.endTime = '17:00';
                }
            }
        },
        'taskData.start_time'() {
            // Recalculate allocated time when start time changes
            this.calculateAllocatedTime();
        },
        'taskData.end_time'() {
            // Recalculate allocated time when end time changes
            this.calculateAllocatedTime();
        }
    },
    methods: {
        updateStartDateTime() {
            // Initialize defaults if needed
            if (!this.startDate) {
                this.startDate = this.$date().format('YYYY-MM-DD');
            }
            if (!this.startTime) {
                this.startTime = '09:00';
            }
            
            const date = this.startDate;
            const time = this.startTime;
            
            // The pickers hold DISPLAY-zone wall clock, but taskData.start_time is
            // held in the BASELINE zone (same as the value the API stores).
            this.taskData.start_time = this.$dateApi(`${date} ${time}:00`).format('YYYY-MM-DD HH:mm:ss');
            this.startTimeMenu = false;
        },
        updateEndDateTime() {
            // Initialize defaults if needed
            if (!this.endDate) {
                this.endDate = this.$date().format('YYYY-MM-DD');
            }
            if (!this.endTime) {
                this.endTime = '17:00';
            }
            
            const date = this.endDate;
            const time = this.endTime;
            
            // See updateStartDateTime: stored in the BASELINE zone.
            this.taskData.end_time = this.$dateApi(`${date} ${time}:00`).format('YYYY-MM-DD HH:mm:ss');
            this.endTimeMenu = false;
        },
        cancelStartTime() {
            // Reset to previous values or clear if no previous value
            if (this.taskData.start_time) {
                const datetime = this.$date(this.taskData.start_time);
                this.startDate = datetime.format('YYYY-MM-DD');
                this.startTime = datetime.format('HH:mm');
            } else {
                this.startDate = null;
                this.startTime = null;
            }
            this.startTimeMenu = false;
        },
        cancelEndTime() {
            // Reset to previous values or clear if no previous value
            if (this.taskData.end_time) {
                const datetime = this.$date(this.taskData.end_time);
                this.endDate = datetime.format('YYYY-MM-DD');
                this.endTime = datetime.format('HH:mm');
            } else {
                this.endDate = null;
                this.endTime = null;
            }
            this.endTimeMenu = false;
        },
        clearStartTime() {
            this.taskData.start_time = null;
            this.startDate = null;
            this.startTime = null;
        },
        clearEndTime() {
            this.taskData.end_time = null;
            this.endDate = null;
            this.endTime = null;
        },
        calculateAllocatedTime() {
            if (!this.taskData.start_time || !this.taskData.end_time) {
                this.taskData.allocated_time = null;
                return;
            }
            
            const start = this.$date(this.taskData.start_time);
            const end = this.$date(this.taskData.end_time);
            
            if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
                this.taskData.allocated_time = null;
                return;
            }
            
            // Calculate duration in milliseconds and convert to hours/minutes
            const diffMs = end.diff(start);
            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            
            // Store as HH:mm:ss format
            const totalHours = String(hours).padStart(2, '0');
            const totalMinutes = String(minutes).padStart(2, '0');
            this.taskData.allocated_time = `${totalHours}:${totalMinutes}:00`;
        },
        customFilter(item, queryText) {
            if (!queryText) return true;

            const searchText = queryText.toLowerCase();
            const searchableText = item.searchableText || '';

            // Search in the combined searchable text
            return searchableText.includes(searchText);
        },
        getRoleMemberCount(roleId) {
            if (!roleId) return 0;
            return this.users.filter(user => user.role_id === roleId).length;
        },
        resetForm() {
            // Determine default status_id
            let defaultStatusId = null;
            if (this.meetingId) {
                // Meeting tasks default to "To Do" (status 1)
                defaultStatusId = 1;
            } else if (this.taskType === 'milestone') {
                // Milestone tasks default to "To Do" (status 1)
                defaultStatusId = 1;
            } else if (this.taskType === 'self' && this.get_user?.role_id === 1) {
                // Super Admin creating own task defaults to "To Do" (status 1)
                defaultStatusId = 1;
            }
            
            // Use prop defaultStatusId if provided, otherwise use calculated default
            if (this.defaultStatusId !== null && this.defaultStatusId !== undefined) {
                defaultStatusId = this.defaultStatusId;
            }
            
            this.taskData = {
                name: this.defaultName || '',
                description: this.defaultDescription || '',
                assignee_ids: [],
                priority_id: this.defaultPriorityId ? Number(this.defaultPriorityId) : null,
                status_id: defaultStatusId,
                due_date: this.defaultDueDate || null,
                start_time: null,
                end_time: null,
                allocated_time: null,
                company_id: this.defaultCompanyId || this.selectedCompany?.id || null,
                goal_id: this.defaultGoalId || null,
                role_id: this.defaultRoleId || null,
                // Recurrence fields
                recurrence_interval: 1,
                recurrence_days: [],
                series_start_date: this.todayDate,
                series_end_date: null
            };
            this.taskNamesText = '';
            this.assigneeSearch = '';
            this.startDate = null;
            this.startTime = null;
            this.endDate = null;
            this.endTime = null;
            this.validationAttempted = false;
            // Apply voice-pre-fill mode and task names
            this.mode = (this.defaultMode === 'multiple' && this.defaultTaskNames.length > 0) ? 'multiple' : 'single';
            if (this.mode === 'multiple' && this.defaultTaskNames.length > 0) {
                this.taskNamesText = this.defaultTaskNames.join('\n');
            }
            this.attachedResources = [];
            this.showAttachResourceDialog = false;
            if (this.$refs.taskForm) {
                this.$refs.taskForm.resetValidation();
            }
            // Set up default assignees after resetting
            this.setupDefaultAssignees();
        },
        setupDefaultAssignees() {
            if (this.taskType === 'self' && this.get_user?.id) {
                this.taskData.assignee_ids = [this.get_user.id];
            } else if (this.taskType === 'assign') {
                // Use defaultAssignees if provided
                if (this.defaultAssignees && this.defaultAssignees.length > 0) {
                    this.taskData.assignee_ids = [...this.defaultAssignees];
                } else {
                    this.taskData.assignee_ids = [];
                }
            } else if (this.defaultAssignees.length > 0) {
                this.taskData.assignee_ids = [...this.defaultAssignees];
            }
        },
        removeAssignee(userId) {
            const index = this.taskData.assignee_ids.indexOf(userId);
            if (index > -1) {
                this.taskData.assignee_ids.splice(index, 1);
            }
        },
        removeTaskName(index) {
            const lines = this.taskNamesText.split('\n');
            lines.splice(index, 1);
            this.taskNamesText = lines.join('\n');
        },
        handleResourcesAttached(resources) {
            // Add newly attached resources to the list
            resources.forEach(resource => {
                const exists = this.attachedResources.some(r => r.id === resource.id);
                if (!exists) {
                    this.attachedResources.push(resource);
                }
            });
            
            this.$toast.success(`${resources.length} resource${resources.length > 1 ? 's' : ''} attached`);
        },
        removeAttachment(resourceId) {
            const index = this.attachedResources.findIndex(r => r.id === resourceId);
            if (index > -1) {
                this.attachedResources.splice(index, 1);
            }
        },
        toggleRecurrenceDay(dayValue) {
            const index = this.taskData.recurrence_days.indexOf(dayValue);
            if (index > -1) {
                this.taskData.recurrence_days.splice(index, 1);
            } else {
                this.taskData.recurrence_days.push(dayValue);
            }
        },
        
        formatDateValue(dateValue) {
            if (!dateValue) return null;
            
            // If it's already in YYYY-MM-DD format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            
            // Otherwise, parse with dayjs and format
            const parsedDate = this.$date(dateValue);
            return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
        },

        selectWeekdays() {
            // Select Monday through Friday (1-5)
            this.taskData.recurrence_days = [1, 2, 3, 4, 5];
        },

        clearAllDays() {
            this.taskData.recurrence_days = [];
        },
        
        closeDialog() {
            this.internalDialog = false;
            this.$emit('close');
        },
        
        formatTimeForApi(datetime) {
            // Extract just the time in H:i format (e.g., "14:30").
            // taskData.start_time/end_time are already held in the BASELINE zone
            // (see updateStartDateTime), so resolve the instant and re-render it
            // in BASELINE rather than in the viewer's display zone.
            if (!datetime) return null;

            const date = this.$dateApi(this.$date(datetime));
            if (!date.isValid()) return null;

            return date.format('HH:mm');
        },
        
        formatAllocatedTimeForApi(allocatedTime) {
            // Convert "HH:mm:ss" format to minutes (integer)
            if (!allocatedTime) return null;
            
            const parts = allocatedTime.split(':');
            if (parts.length !== 3) return null;
            
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            
            return (hours * 60) + minutes;
        },
        
        async createTasks() {
            this.validationAttempted = true;

            if (!this.canCreate) {
                this.$toast.error("Please fill in all required fields");
                return;
            }

            this.loading = true;

            try {
                let tasksToCreate = [];

                // Prepare attachments for API - convert to backend structure
                const attachments = this.attachedResources.map(resource => ({
                    resource_id: resource.id,
                    file_name: resource.name,
                    file_url: resource.url,
                    file_type: resource.file_type,
                    file_size: resource.size,
                    mime_type: resource.mime_type || this.getMimeTypeFromFileType(resource.file_type),
                    category: resource.category,
                    created_by: this.get_user.id
                }));

                // Check if user is CEO or COO
                const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
                const cooEmail = process.env.VUE_APP_COO_EMAIL;
                const userEmail = this.get_user?.email?.toLowerCase();
                const isCeoOrCoo = userEmail && (
                    (ceoEmail && userEmail === ceoEmail.toLowerCase()) || 
                    (cooEmail && userEmail === cooEmail.toLowerCase())
                );

                // Determine if user should get status 1 (approved) or 8 (pending approval)
                // Status 1 if: meeting task, CEO/COO, self-task, or manager assigning to their managed role
                // Status 8 otherwise (requires approval)
                const canBypassApproval = this.meetingId || isCeoOrCoo || this.taskType === 'self' || 
                    (this.isCurrentUserManager && this.isManagerOfSelectedRole);
                
                let defaultStatusId = this.taskData.status_id || (canBypassApproval ? 1 : 8);

                if (this.mode === 'single') {
                    tasksToCreate = [{
                        name: this.taskData.name,
                        description: this.taskData.description,
                        assignee_ids: this.taskData.assignee_ids || [],
                        priority_id: this.taskData.priority_id,
                        status_id: defaultStatusId,
                        due_date: this.taskData.due_date,
                        company_id: this.taskData.company_id || this.selectedCompany?.id,
                        role_id: this.taskData.role_id,
                        user_role_id: this.taskData.role_id, // Assignee's role_id (from role selection)
                        meeting_id: this.meetingId,
                        ...(this.taskType === 'milestone' && { goal_id: this.taskData.goal_id }),
                        attachments: attachments,
                        // Include time fields
                        start_time: this.formatTimeForApi(this.taskData.start_time),
                        end_time: this.formatTimeForApi(this.taskData.end_time),
                        allocated_time: this.formatAllocatedTimeForApi(this.taskData.allocated_time),
                        // Include recurrence fields if status is recurring (7)
                        ...(this.taskData.status_id === 7 && {
                            recurrence_interval: this.taskData.recurrence_interval,
                            recurrence_days: this.taskData.recurrence_days,
                            series_start_date: this.formatDateValue(this.taskData.series_start_date),
                            series_end_date: this.formatDateValue(this.taskData.series_end_date)
                        })
                    }];
                } else {
                    tasksToCreate = this.taskNamesList.map(name => ({
                        name: name,
                        description: this.taskData.description,
                        assignee_ids: this.taskData.assignee_ids || [],
                        priority_id: this.taskData.priority_id,
                        status_id: defaultStatusId,
                        due_date: this.taskData.due_date,
                        company_id: this.taskData.company_id || this.selectedCompany?.id,
                        role_id: this.taskData.role_id,
                        user_role_id: this.taskData.role_id, // Assignee's role_id (from role selection)
                        meeting_id: this.meetingId,
                        ...(this.taskType === 'milestone' && { goal_id: this.taskData.goal_id }),
                        attachments: attachments,
                        // Include time fields
                        start_time: this.formatTimeForApi(this.taskData.start_time),
                        end_time: this.formatTimeForApi(this.taskData.end_time),
                        allocated_time: this.formatAllocatedTimeForApi(this.taskData.allocated_time),
                        // Include recurrence fields if status is recurring (7)
                        ...(this.taskData.status_id === 7 && {
                            recurrence_interval: this.taskData.recurrence_interval,
                            recurrence_days: this.taskData.recurrence_days,
                            series_start_date: this.formatDateValue(this.taskData.series_start_date),
                            series_end_date: this.formatDateValue(this.taskData.series_end_date)
                        })
                    }));
                }

                const response = await this.$axios.post('api/tasks/create_task_v2', {
                    tasks: tasksToCreate
                });

                if (response.data.response) {
                    const createdCount = tasksToCreate.length;
                    this.$toast.success(`${createdCount} task${createdCount > 1 ? 's' : ''} created successfully`);
                    this.closeDialog();
                    // Emit the created tasks with generated IDs for optimistic updates
                    const createdTasks = response.data.data || tasksToCreate.map((task, index) => ({
                        ...task,
                        id: Date.now() + index, // Temporary ID until refresh
                        created_at: new Date().toISOString(),
                        creator: this.get_user,
                        assignees: task.assignee_ids?.map(id => {
                            const user = this.allUsers.find(u => u.id === id);
                            return { user_id: id, user: user };
                        }) || []
                    }));
                    this.$emit('tasks-created', createdTasks);
                } else {
                    this.$toast.error(response.data.message || 'Failed to create tasks');
                }
            } catch (error) {
                console.error('Error creating tasks:', error);
                this.$toast.error('Failed to create tasks');
            } finally {
                this.loading = false;
            }
        },
        getInitials(name) {
            return name
                ? name.split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : "?";
        },
        getAvatarColor(name) {
            if (!name) return '#9E9E9E';

            const key = name.substring(0, 3).toUpperCase();
            let hash = 0;
            for (let i = 0; i < key.length; i++) {
                hash = key.charCodeAt(i) + ((hash << 5) - hash);
            }

            const colors = [
                '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
                '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
                '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
                '#A9DFBF', '#F9E79F', '#D5A6BD', '#AED6F1', '#A3E4D7',
                '#FCF3CF', '#FADBD8', '#D6EAF8', '#E8DAEF', '#D1F2EB'
            ];

            const colorIndex = Math.abs(hash) % colors.length;
            return colors[colorIndex];
        },
        getPriorityChipColor(priorityId) {
            const colorMap = {
                1: '#9C27B0', // Urgent - Purple
                2: '#F44336', // High - Red
                3: '#FF9800', // Medium - Orange
                4: '#4CAF50', // Low - Green
                null: '#9E9E9E'    // None - Grey
            };
            return colorMap[priorityId] || '#9E9E9E';
        },
        getPriorityTextColor(priorityId) {
            return priorityId ? 'white' : 'rgba(0,0,0,0.6)';
        },
        getStatusChipColor(statusId) {
            const colorMap = {
                1: '#9E9E9E',      // To Do - Grey
                2: '#2196F3',      // In Progress - Blue
                3: '#4CAF50',      // In Review - Green
                4: '#4CAF50',      // Completed - Green
                5: '#FF9800',      // Blocked - Orange
                6: '#795548',      // Cancelled - Brown
                7: '#2196F3',      // Recurring - Blue
                null: '#9E9E9E'    // None - Grey
            };
            return colorMap[statusId] || '#9E9E9E';
        },
        getStatusTextColor(statusId) {
            const lightStatuses = [null];
            return lightStatuses.includes(statusId) ? 'rgba(0,0,0,0.6)' : 'white';
        },
        getDialogTitle() {
            if (this.taskType === 'milestone') {
                return this.mode === 'single' ? 'Create Milestone' : 'Create Multiple Milestones';
            } else if (this.taskType === 'self') {
                return this.mode === 'single' ? 'Create Own Task' : 'Create Multiple Own Tasks';
            } else if (this.defaultAssignees && this.defaultAssignees.length > 0) {
                const assigneeName = this.getAssigneeName();
                if (assigneeName) {
                    return this.mode === 'single' ? `Assign Task to ${assigneeName}` : `Assign Multiple Tasks to ${assigneeName}`;
                }
            }
            return this.mode === 'single' ? 'Create Task' : 'Create Multiple Tasks';
        },
        getAssigneeName() {
            if (this.defaultAssignees && this.defaultAssignees.length === 1) {
                const assigneeId = this.defaultAssignees[0];
                const assignee = this.availableUsers.find(user => user.id === assigneeId);
                return assignee?.name;
            }
            return null;
        },
        getCreateButtonText() {
            if (this.taskType === 'self') {
                return this.mode === 'single' ? 'Create Own Task' : `Create ${this.taskNamesList.length} Own Tasks`;
            } else if (this.defaultAssignees && this.defaultAssignees.length > 0) {
                const assigneeName = this.getAssigneeName();
                if (assigneeName) {
                    return this.mode === 'single' ? `Assign to ${assigneeName}` : `Assign ${this.taskNamesList.length} Tasks`;
                }
            }
            return this.mode === 'single' ? 'Create Task' : `Create ${this.taskNamesList.length} Tasks`;
        },
        // File type helper methods for attachments
        getFileTypeIcon(fileType) {
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                image: 'mdi-file-image-box',
                video: 'mdi-file-video-box',
                audio: 'mdi-file-music-box'
            };
            return iconMap[fileType] || 'mdi-file-document-outline';
        },
        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                image: 'pink',
                video: 'deep-purple',
                audio: 'teal'
            };
            return colorMap[fileType] || 'grey';
        },
        getMimeTypeFromFileType(fileType) {
            const mimeMap = {
                pdf: 'application/pdf',
                doc: 'application/msword',
                docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                xls: 'application/vnd.ms-excel',
                xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ppt: 'application/vnd.ms-powerpoint',
                pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                txt: 'text/plain',
                zip: 'application/zip',
                image: 'image/jpeg',
                video: 'video/mp4',
                audio: 'audio/mpeg'
            };
            return mimeMap[fileType] || 'application/octet-stream';
        }
    }
};
</script>

<style lang="scss" scoped>
.assignee-initials {
    font-size: 10px;
    font-weight: 600;
}

.headline {
    background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
    color: white;

    .v-icon {
        color: white;
    }

    .v-btn-toggle {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 6px;

        .v-btn {
            color: rgba(255, 255, 255, 0.7);
            background: transparent;

            &.v-btn--active {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }
        }
    }
}

:deep(.v-chip.v-chip--outlined) {
    border-color: #e0e0e0;
}

:deep(.v-text-field .v-input__slot) {
    background: #fafafa;
}

:deep(.v-textarea .v-input__slot) {
    background: #fafafa;
}

:deep(.v-select .v-input__slot) {
    background: #fafafa;
}

:deep(.v-autocomplete .v-input__slot) {
    background: #fafafa;
}

.v-chip {

    &.priority-chip,
    &.status-chip {
        font-weight: 500;
    }
}

.user-role-info {
    display: flex;
    flex-direction: column;
    line-height: 1.2;

    .role-position {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: 500;
        margin-bottom: 2px;
    }

    .user-email {
        font-size: 11px;
        color: rgba(0, 0, 0, 0.4);
    }
}

.attachment-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e9ecef;
}

.attach-btn {
    text-transform: none;
    font-weight: 500;
}

.attached-resources {
    max-height: 120px;
    overflow-y: auto;
}

.attachment-chip {
    margin: 2px;
}

.no-attachments {
    padding: 12px 0;
    text-align: center;
    border: 2px dashed #dee2e6;
    border-radius: 6px;
    background: #fafbfc;
}
</style>